import ENV from 'src/env.json';
import React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { BrowserOptions } from '@sentry/browser/types/client';

function initSentry(): void {
  if (ENV.NODE_ENV === 'dev') {
    return;
  }

  const options: BrowserOptions = {
    dsn: ENV.SENTRY_DSN,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: ENV.SENTRY_TRACES_SAMPLE_RATE,
    environment: ENV.NODE_ENV,
    // Ignore known browser/third-party errors we cannot fix
    // Organized by category for maintainability
    ignoreErrors: [
      // ============================================
      // BROWSER EXTENSIONS & THIRD-PARTY SCRIPTS
      // ============================================
      /runtime\.sendMessage/,
      'Invalid call to runtime.sendMessage',
      /window\.webkit\.messageHandlers/,
      "undefined is not an object (evaluating 'window.webkit.messageHandlers')",
      /undefined is not an object \(evaluating 'window\.webkit/,
      /extendPostHogWithExceptionAutocapture/,
      /Can't find variable: EmptyRanges/, // CodeMirror browser extensions (CUSTOMER-WEB-APP-NX)
      /Event `CustomEvent`.*captured as promise rejection/, // Third-party scripts (CUSTOMER-WEB-APP-FR)
      /CustomEvent.*unhandledrejection/,

      // ============================================
      // NETWORK & MODULE LOADING ERRORS
      // ============================================
      /Failed to fetch dynamically imported module/,
      /error loading dynamically imported module/i, // Fixes CUSTOMER-WEB-APP-P4
      /Importing a module script failed/,
      /Failed to load.*cdnjs\.cloudflare\.com/,
      "'text/html' is not a valid JavaScript MIME type",

      // ============================================
      // REQUEST ABORT & TIMEOUT ERRORS
      // ============================================
      /ECONNABORTED/, // Fixes CUSTOMER-WEB-APP-P3
      /Request aborted/,

      // ============================================
      // SAFARI PRIVATE MODE & STORAGE ERRORS
      // ============================================
      /null is not an object \(evaluating 'localStorage/,
      /null is not an object \(evaluating 'l\.removeItem/,

      // ============================================
      // REACT/REACTSTRAP RACE CONDITIONS
      // ============================================
      'this.context.toggle is not a function',
      /toggle is not a function/,
      /Minified invariant #/,

      // ============================================
      // PUSHER/WEBSOCKET ERRORS (CUSTOMER-WEB-APP-2J)
      // ============================================
      /handshakeCallbacks.*is not a function/, // Pusher internal handshake error
      /\$element is not connected/, // DOM element unmounted during operation

      // ============================================
      // THIRD-PARTY SDK ERRORS
      // ============================================
      /Object Not Found Matching Id.*MethodName/, // IndexedDB/SignalR-style SDK errors
      /t is not a function.*is an instance of Promise/, // Minified async race condition

      // ============================================
      // REACT NATIVE FABRIC (MOBILE APP)
      // ============================================
      /RetryableMountingLayerException/, // Fixes CUSTOMER-WEB-APP-P0
      /Unable to find viewState for tag/,

      // ============================================
      // JSON PARSING & PROMISE ERRORS
      // ============================================
      'Unexpected end of JSON input',
      /SyntaxError: Unexpected end of JSON/,
      /Object captured as promise rejection/,

      // ============================================
      // BROWSER COMPATIBILITY
      // ============================================
      /NotSupportedError: The operation is not supported/, // CUSTOMER-WEB-APP-NV
    ],
    // Ignore errors from third-party scripts
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Firefox extensions
      /^moz-extension:\/\//i,
      // Safari extensions
      /^safari-extension:\/\//i,
      // Third-party CDNs that might fail
      /cdnjs\.cloudflare\.com/i,
      // Fixes CUSTOMER-WEB-APP-K1: User-guiding analytics script
      /user-guiding/i,
      /userguiding/i,
      // TikTok embed SDK - Fixes CUSTOMER-WEB-APP-MV
      /tiktok_web_login_static/i,
      /tiktok.*falcon.*embed/i,
    ],
    // Filter out errors with no actionable first-party stacktrace
    // Fixes CUSTOMER-WEB-APP-GD: "t is not a function" from anonymous/third-party scripts
    beforeSend(event) {
      // Check if the error has any first-party frames in the stacktrace
      const frames = event.exception?.values?.[0]?.stacktrace?.frames;
      if (frames && frames.length > 0) {
        const hasFirstPartyFrame = frames.some((frame) => {
          const filename = frame.filename || '';
          return (
            filename.includes('space.becreatives.co') ||
            filename.includes('/src/') ||
            filename.includes('/js/') ||
            filename.startsWith('app://')
          );
        });
        // If all frames are anonymous/third-party, drop the event
        if (!hasFirstPartyFrame) {
          return null;
        }
      }
      return event;
    },
  };

  const release = import.meta.env.VITE_COMMIT_SHA;

  if (release) {
    options['release'] = release;
  } else {
    console.warn('Incomplete sentry configuration - VITE_COMMIT_SHA env var is not defined');
  }

  Sentry.init(options);
}

export { initSentry };
